"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useState } from "react";

/**
 * At most this many spotlights in one sitting. Two modals back to back is an
 * announcement; three is a tour nobody asked for.
 */
export const SPOTLIGHTS_PER_SITTING = 2;

/** A sitting is one tab session. */
const STORAGE_KEY = "feed-spotlight:sitting";

/** The in-memory count, for when sessionStorage throws (a private window, blocked site data). */
let memoryCount = 0;
export function readSittingCount(): number {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const stored = raw === null ? 0 : Number.parseInt(raw, 10);
    return Number.isFinite(stored) ? Math.max(stored, memoryCount) : memoryCount;
  } catch {
    return memoryCount;
  }
}
export function recordSittingShow(): number {
  const next = readSittingCount() + 1;
  memoryCount = next;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, String(next));
  } catch {
    // The in-memory count above still caps this tab.
  }
  return next;
}

/** Test seam: the module-level fallback survives remounts by design. */
export function resetSittingForTest(): void {
  memoryCount = 0;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** How many spotlights this tab has shown, and how to count one more. Survives remounts. */
export function useSpotlightSitting() {
  const $ = _c(4);
  const [count, setCount] = useState(_temp);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => setCount(recordSittingShow());
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const record = t0;
  const t1 = Math.max(0, SPOTLIGHTS_PER_SITTING - count);
  let t2;
  if ($[1] !== count || $[2] !== t1) {
    t2 = {
      count,
      room: t1,
      record
    };
    $[1] = count;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp() {
  return typeof window === "undefined" ? 0 : readSittingCount();
}